import { create } from 'zustand';
import { useToastStore } from './toast-store';

// Port of src/app/components/nav-bar/nav-bar.service.ts (NavBarService — the
// nav-bar state holder). The other services in that folder
// (services/animation/prompter-animation.service.ts and
// services/input/search-input-helper.service.ts) are behavior/helper services
// owned by the nav-bar component port, not state — not ported here.

const MAX_FILE_SIZE = 25;

interface NavBarState {
  fileUploadResponse: { key: string; value: string }[] | undefined;
  imagePreview: string | undefined;
  videoPreview: string | undefined;
  uploadError: string | undefined;
  file: File | null;
  createPopupSignal: boolean;
  uploadStarted: boolean;
  isImageFile: boolean;
  isVideoFile: boolean;
  isAudioFile: boolean;
  uploadFailed: boolean;
  fileUploadComplete: boolean;
  uploadProgress: number;
  // Search Dropdown State (Angular: private signal + readonly computed)
  searchDropDownHidden: boolean;

  setSearchDropDownHidden: (value: boolean) => void;
  resetFileState: () => void;
  /**
   * NOTE(port): Angular signature was
   * `changeFile(isMobileDevice: WritableSignal<boolean>, toast: ToastService, file: File | undefined)`.
   * Here `isMobileDevice` is a plain boolean (read it from useDeviceStore) and
   * the toast comes from useToastStore directly.
   */
  changeFile: (isMobileDevice: boolean, file: File | undefined) => boolean;

  // Generic setter for the fields Angular components wrote to directly via the
  // service's signals (e.g. uploadStarted.set(true)).
  setNavBarField: <K extends keyof NavBarState>(key: K, value: NavBarState[K]) => void;
}

function previewVideo(file: File) {
  useNavBarStore.setState({ videoPreview: URL.createObjectURL(file) });
}

function previewImage(file: File) {
  const reader = new FileReader();
  reader.onload = (e: any) => {
    useNavBarStore.setState({ imagePreview: e.target.result });
  };
  reader.readAsDataURL(file);
}

function captureVideoScreenshot(file: File) {
  const video = document.createElement('video');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  video.src = URL.createObjectURL(file);
  video.load();
  video.onloadeddata = () => {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    video.currentTime = 1; // Capture the screenshot at the 1-second mark
  };

  video.onseeked = () => {
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      useNavBarStore.setState({ imagePreview: canvas.toDataURL('image/png') });
      URL.revokeObjectURL(video.src); // Clean up
    }
  };
}

export const useNavBarStore = create<NavBarState>()((set, get) => ({
  fileUploadResponse: undefined,
  imagePreview: undefined,
  videoPreview: undefined,
  uploadError: undefined,
  file: null,
  createPopupSignal: false,
  uploadStarted: false,
  isImageFile: false,
  isVideoFile: false,
  isAudioFile: false,
  uploadFailed: false,
  fileUploadComplete: false,
  uploadProgress: 0,
  searchDropDownHidden: true,

  setSearchDropDownHidden: (value) => set({ searchDropDownHidden: value }),

  resetFileState: () => set({
    file: null,
    isImageFile: false,
    isVideoFile: false,
    isAudioFile: false,
    uploadProgress: 0,
  }),

  changeFile: (isMobileDevice, file) => {
    if (file && file.size <= MAX_FILE_SIZE * 1024 * 1024) {
      set({
        file,
        isImageFile: file.type.startsWith('image'),
        isVideoFile: file.type.startsWith('video'),
        isAudioFile: file.type.startsWith('audio'),
      });

      if (get().isImageFile) {
        previewImage(file);
      } else if (get().isVideoFile) {
        captureVideoScreenshot(file);
        previewVideo(file);
      }

      if (!isMobileDevice) {
        set({ createPopupSignal: true });
      }

      return true;
    }

    useToastStore.getState().error('File size exceeds the limit of 25MB');
    return false;
  },

  setNavBarField: (key, value) => set({ [key]: value } as Pick<NavBarState, typeof key>),
}));
